<template>
  <div>
    <card-wrapper
      :title="$t('offer-manager.offers-list.title')"
      :action-button-data="{title: $t('title.create-offer'), icon: 'fak fa-light fa-m-file-plus'}"
      id="offer-manager-list"
      @on-open-action-button-dialog="handleOfferDialogOpenClose">
      <offers-filters
        :statuses="statuses"
        :text="text"
        :show-expired="showExpired"
        @update:statuses="statuses = $event"
        @update:text="text = $event"
        @update:showExpired="showExpired = $event"
        @submit="onFiltersSubmit" />

      <expiring-offers-section
        :dealer-id="group.dealerId"
        :statuses="statuses"
        :text="text"
        @changeStatus="changeStatus"
        ref="expiringSection" />

      <h3 class="all-offers-title">{{ $t('offers.all-offers.title') }}</h3>

      <offer-table
        :statuses="statuses"
        :text="text"
        :show-expired="showExpired"
        @changeStatus="changeStatus"
        @refreshList="refreshOffersList"
        ref="offerTable" />

      <reject-offer-dialog
        :is-dialog-open="showRejectDialog"
        :loading="loading"
        @reject="onRejectAndRefresh"
        @close="onRejectClose" />

      <create-offer-dialog :isDialogOpen="isOfferDialogOpen" @onClose="handleOfferDialogOpenClose()" />
    </card-wrapper>
  </div>
</template>

<script lang="ts">
import {defineComponent, ref} from '@vue/composition-api'

import CardWrapper from '@/components/general/CardWrapper.vue'
import CreateOfferDialog from '@/components/offer-manager/CreateOfferDialog.vue'
import ExpiringOffersSection from '@/components/offer-manager/ExpiringOffersSection.vue'
import OffersFilters from '@/components/offer-manager/OffersFilters.vue'
import RejectOfferDialog from '@/components/offer-manager/RejectOfferDialog.vue'
import {useOfferChangeStatus} from '@/composables/offer-change-status'
import {Status} from '@/types/offers'
import {mapGetters} from '@/utils/map-store'

import OfferTable from './OfferTable.vue'

export default defineComponent({
  name: 'Offers',
  components: {
    CardWrapper,
    CreateOfferDialog,
    ExpiringOffersSection,
    OfferTable,
    OffersFilters,
    RejectOfferDialog,
  },
  setup(_, {root}) {
    const {getGroup: group} = mapGetters(root.$store.getters)
    const isOfferDialogOpen = ref(false)
    const expiringSection = ref<any>(null)
    const offerTable = ref<any>(null)

    const statuses = ref<Status[]>([
      Status.Pending,
      Status.Accepted,
      Status.COICreated,
      Status.Quote,
      Status.Ordered,
      Status.Invoiced,
    ])
    const text = ref('')
    const showExpired = ref(false)

    function refreshAll() {
      root.$store.dispatch('loadOffersList', group.value.dealerId)
      offerTable.value?.refetch?.()
      expiringSection.value?.refetch?.()
    }

    function refreshOffersList() {
      root.$store.dispatch('loadOffersList', group.value.dealerId)
      expiringSection.value?.refetch?.()
    }

    const {loading, showRejectDialog, changeStatus, onRejectConfirm, onRejectClose} = useOfferChangeStatus({
      dealerId: group.value.dealerId,
      onChanged: refreshAll,
      router: root.$router,
    })

    function handleOfferDialogOpenClose() {
      isOfferDialogOpen.value = !isOfferDialogOpen.value
    }

    function onFiltersSubmit() {
      offerTable.value?.refetch?.()
      expiringSection.value?.refetch?.()
    }

    async function onRejectAndRefresh(payload: any) {
      await onRejectConfirm(payload)
    }

    return {
      group,
      isOfferDialogOpen,
      statuses,
      text,
      showExpired,
      expiringSection,
      offerTable,
      loading,
      showRejectDialog,
      changeStatus,
      onRejectClose,
      onRejectAndRefresh,
      refreshOffersList,
      handleOfferDialogOpenClose,
      onFiltersSubmit,
    }
  },
})
</script>

<style scoped>
.all-offers-title {
  font:
    normal normal 600 24px/30px 'ProximaNovaExCnSBold',
    sans-serif;
  text-transform: uppercase;
  color: var(--v-primary-base, #dc182d);
  margin: 24px 0 0;
  letter-spacing: 0;
}
</style>
